import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Mail, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import ProviderProbeControls from '@/components/admin/ProviderProbeControls';

/** Admin-editable Resend sender identity (iter-10).
 *  Stored in `app_config`, falls back to env `SENDER_EMAIL` when blank.
 *  Domain verification happens externally at resend.com/domains. */
export default function EmailSenderConfig({ cfg, onSaved }) {
  const [sender, setSender] = useState('');
  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [resendAlertEmail, setResendAlertEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  /* 2026-02 · Persist the last send-test response so admins see the
   * resolved From: address + Resend message ID + subdomain state
   * without leaving the panel — critical while cutting over the
   * send.crownnrose.com DKIM/SPF records. */
  const [lastSend, setLastSend] = useState(null);

  useEffect(() => {
    if (!cfg) return;
    setSender(cfg.email_sender || '');
    setSenderName(cfg.email_sender_name || '');
    setReplyTo(cfg.email_reply_to || '');
    setResendAlertEmail(cfg.resend_alert_email || '');
  }, [cfg]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({
        email_sender: sender.trim(),
        email_sender_name: senderName.trim(),
        email_reply_to: replyTo.trim(),
      });
      toast.success('Email sender saved.');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save email sender.');
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    const to = testTo.trim();
    if (!to || !to.includes('@')) { toast.error('Enter a recipient email.'); return; }
    setTesting(true);
    setLastSend(null);
    try {
      const data = await adminApi.testEmail(to);
      setLastSend(data);
      toast.success(`Test email sent to ${to}. Check the inbox + spam folder.`);
    } catch (e) {
      // Resilient detail extraction: server may send JSON {detail}, a string,
      // or in rare ingress-rewrite cases an HTML body. Surface whatever is
      // most useful to the admin.
      const data = e?.response?.data;
      let detail = (data && typeof data === 'object' && data.detail)
        || (typeof data === 'string' ? data : '')
        || e?.message
        || 'Test send failed.';
      if (typeof detail === 'string' && detail.length > 400) detail = detail.slice(0, 400) + '…';
      toast.error(detail);
    } finally { setTesting(false); }
  };

  const isVerifiedDomain = sender && !/onboarding@resend\.dev/i.test(sender) && sender.includes('@');
  // Lightweight email validity for the Send-test button — Resend itself will
  // do the final validation; we just guard against obvious typos client-side.
  const testToValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo.trim());

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-email-sender">
      <div className="flex items-start gap-2">
        <Mail className="mt-0.5 h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Email Sender (Resend)</div>
          <div className="text-[11px] text-muted-foreground">
            The &ldquo;from&rdquo; identity used by transactional emails (e.g. admin password rotations).
            To send to recipients other than the Resend account owner, verify a sending domain at{' '}
            <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com/domains</a>{' '}
            and put a matching address below.
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <Label className="text-[11px]">Sender email address</Label>
          <Input
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            placeholder="admin@yourdomain.com"
            className="mt-1 h-8 font-mono-stat text-[12px]"
            data-testid="admin-email-sender-input"
          />
          <div className="mt-1 text-[10.5px] text-muted-foreground">
            Leave blank to fall back to the env <code>SENDER_EMAIL</code> (defaults to <code>onboarding@resend.dev</code>).
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Display name</Label>
          <Input
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder="The Loom Admin"
            className="mt-1 h-8 text-[12px]"
            data-testid="admin-email-sender-name"
          />
          <div className="mt-1 text-[10.5px] text-muted-foreground">
            Combined as <code>{senderName || 'The Loom'} &lt;{sender || 'admin@your.tld'}&gt;</code>.
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Reply-to (optional)</Label>
          <Input
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="support@yourdomain.com"
            className="mt-1 h-8 font-mono-stat text-[12px]"
            data-testid="admin-email-replyto"
          />
        </div>
        <div className="flex items-end">
          <Button
            size="sm"
            onClick={save}
            disabled={busy}
            data-testid="admin-email-sender-save"
            className="h-8 gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save sender
          </Button>
        </div>
      </div>

      {isVerifiedDomain && (
        <div className="mt-3 rounded-sm border border-success/40 bg-success/5 px-3 py-2 text-[11px] text-success">
          ✓ Sender domain looks production-ready. Make sure it&apos;s verified at resend.com/domains.
        </div>
      )}
      {!sender && (
        <div className="mt-3 rounded-sm border border-warning/40 bg-warning/5 px-3 py-2 text-[11px] text-warning-foreground">
          Currently using Resend test mode (onboarding@resend.dev). Emails only deliver to the account-owner&apos;s verified address.
        </div>
      )}

      <ProviderProbeControls
        testEndpoint="/admin/resend/test-connection"
        alertEmail={resendAlertEmail}
        onAlertEmailChange={setResendAlertEmail}
        onAlertEmailSave={async () => {
          await adminApi.saveConfig({ resend_alert_email: resendAlertEmail.trim() });
          onSaved?.();
        }}
        lastTestAt={cfg?.resend_last_test_at || ''}
        lastTestOk={cfg?.resend_last_test_ok}
        lastTestMessage={cfg?.resend_last_test_message || ''}
        configured={true}
        testIdPrefix="admin-resend"
        onAfterTest={onSaved}
      />

      <div className="mt-4 rounded-sm border border-dashed border-border bg-muted/30 p-3">
        <div className="text-[12px] font-semibold flex items-center gap-1.5">
          <Send className="h-3.5 w-3.5 text-primary" /> Verify domain — send a test
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          After verifying your domain at <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com/domains</a>{' '}
          and saving the sender above, send yourself a one-line test. A successful
          delivery (not bounced, not stuck in spam) means production emails will reach any recipient.
        </p>
        <div className="mt-2 flex gap-2">
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@yourdomain.com"
            className="h-8 max-w-md text-[12px]"
            data-testid="admin-email-test-input"
            onKeyDown={(e) => e.key === 'Enter' && sendTest()}
          />
          <Button
            size="sm"
            onClick={sendTest}
            disabled={testing || !testToValid}
            className="h-8 gap-1.5"
            data-testid="admin-email-test-send"
          >
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send test
          </Button>
        </div>
        {/* 2026-02 · Resolved-headers panel — surfaces the exact From:
         *  the recipient will see, the Resend message ID, and the
         *  send-subdomain state so admins can verify the DNS cutover
         *  without opening Resend's dashboard. */}
        {lastSend && (
          <div className="mt-3 rounded-sm border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[11px] leading-relaxed"
                data-testid="admin-email-test-result">
            <div className="flex items-center gap-1.5 font-semibold text-emerald-700 dark:text-emerald-400">
              <Send className="h-3 w-3" /> Delivered — headers the recipient will see
            </div>
            <dl className="mt-1.5 grid grid-cols-[110px_1fr] gap-x-2 gap-y-0.5 font-mono text-[10.5px]">
              <dt className="text-muted-foreground">From:</dt>
              <dd data-testid="admin-email-test-from">{lastSend.resolved_from || '—'}</dd>
              {lastSend.resolved_reply_to && (<>
                <dt className="text-muted-foreground">Reply-To:</dt>
                <dd data-testid="admin-email-test-replyto">{lastSend.resolved_reply_to}</dd>
              </>)}
              <dt className="text-muted-foreground">Provider:</dt>
              <dd>{lastSend.provider}</dd>
              <dt className="text-muted-foreground">Message ID:</dt>
              <dd data-testid="admin-email-test-msgid">{lastSend.id || '(none)'}</dd>
              <dt className="text-muted-foreground">Send domain:</dt>
              <dd data-testid="admin-email-test-subdomain">
                {lastSend.send_subdomain
                  ? <span className="text-emerald-700 dark:text-emerald-400">{lastSend.send_subdomain}</span>
                  : <span className="text-amber-700 dark:text-amber-400">not set — parent domain</span>}
              </dd>
            </dl>
            {lastSend.sandbox_sender && (
              <div className="mt-1.5 text-amber-700 dark:text-amber-400">
                ⚠ Sandbox sender in use — Resend will only deliver to the account owner&apos;s verified address until you save a real sender above.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
